"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PresenceHandler = void 0;
var constants_1 = require("../constants");
var emitter_1 = require("../util/emitter");
var bulk_subscription_service_1 = require("../util/bulk-subscription-service");
var ONLY_EVENT = 'OE';
function validateQueryArguments(rest) {
    var users = null;
    var callback = null;
    if (rest.length === 1) {
        if (Array.isArray(rest[0])) {
            users = rest[0];
        }
        else {
            if (typeof rest[0] !== 'function') {
                throw new Error('invalid argument: "callback"');
            }
            callback = rest[0];
        }
    }
    else if (rest.length === 2) {
        users = rest[0];
        callback = rest[1];
        if (!Array.isArray(users) || typeof callback !== 'function') {
            throw new Error('invalid argument: "users" or "callback"');
        }
    }
    return { users: users, callback: callback };
}
var PresenceHandler = /** @class */ (function () {
    function PresenceHandler(services, options) {
        this.services = services;
        this.globalSubscriptionEmitter = new emitter_1.Emitter();
        this.subscriptionEmitter = new emitter_1.Emitter();
        this.queryEmitter = new emitter_1.Emitter();
        this.queryAllEmitter = new emitter_1.Emitter();
        this.counter = 0;
        this.limboQueue = [];
        this.bulkSubscription = new bulk_subscription_service_1.BulkSubscriptionService(this.services, options.subscriptionInterval, constants_1.TOPIC.PRESENCE, constants_1.PRESENCE_ACTION.SUBSCRIBE, constants_1.PRESENCE_ACTION.UNSUBSCRIBE, this.onBulkSubscriptionSent.bind(this));
        this.services.connection.registerHandler(constants_1.TOPIC.PRESENCE, this.handle.bind(this));
        this.services.connection.onExitLimbo(this.onExitLimbo.bind(this));
        this.services.connection.onLost(this.onExitLimbo.bind(this));
        this.services.connection.onReestablished(this.onConnectionReestablished.bind(this));
    }
    PresenceHandler.prototype.subscribe = function (userOrCallback, callback) {
        if (typeof userOrCallback === 'string' && userOrCallback.length > 0 && typeof callback === 'function') {
            var user = userOrCallback;
            if (!this.subscriptionEmitter.hasListeners(user)) {
                this.bulkSubscription.subscribe(user);
            }
            this.subscriptionEmitter.on(user, callback);
            return;
        }
        if (typeof userOrCallback === 'function' && typeof callback === 'undefined') {
            if (!this.globalSubscriptionEmitter.hasListeners(ONLY_EVENT)) {
                this.subscribeToAllChanges();
            }
            this.globalSubscriptionEmitter.on(ONLY_EVENT, userOrCallback);
            return;
        }
        throw new Error('invalid arguments: "user" or "callback"');
    };
    PresenceHandler.prototype.unsubscribe = function (userOrCallback, callback) {
        if (userOrCallback && typeof userOrCallback === 'string' && userOrCallback.length > 0) {
            var user = userOrCallback;
            if (callback) {
                if (typeof callback !== 'function') {
                    throw new Error('invalid argument: "callback"');
                }
                this.subscriptionEmitter.off(user, callback);
            }
            else {
                this.subscriptionEmitter.off(user);
            }
            if (!this.subscriptionEmitter.hasListeners(user)) {
                this.bulkSubscription.unsubscribe(user);
                return;
            }
        }
        if (userOrCallback && typeof userOrCallback === 'function') {
            callback = userOrCallback;
            this.globalSubscriptionEmitter.off(ONLY_EVENT, callback);
            if (!this.globalSubscriptionEmitter.hasListeners(ONLY_EVENT)) {
                this.unsubscribeToAllChanges();
            }
            return;
        }
        if (typeof userOrCallback === 'undefined' && typeof callback === 'undefined') {
            this.subscriptionEmitter.off();
            this.globalSubscriptionEmitter.off();
            this.bulkSubscription.unsubscribeList(this.subscriptionEmitter.eventNames());
            this.unsubscribeToAllChanges();
            return;
        }
        throw new Error('invalid argument: "user" or "callback"');
    };
    PresenceHandler.prototype.getAll = function () {
        var rest = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            rest[_i] = arguments[_i];
        }
        var _a = validateQueryArguments(rest), callback = _a.callback, users = _a.users;
        var message;
        var emitter;
        var emitterAction;
        if (users) {
            var queryId = (this.counter++).toString();
            message = {
                topic: constants_1.TOPIC.PRESENCE,
                action: constants_1.PRESENCE_ACTION.QUERY,
                correlationId: queryId,
                names: users
            };
            emitter = this.queryEmitter;
            emitterAction = queryId;
        }
        else {
            message = {
                topic: constants_1.TOPIC.PRESENCE,
                action: constants_1.PRESENCE_ACTION.QUERY_ALL
            };
            emitter = this.queryAllEmitter;
            emitterAction = ONLY_EVENT;
        }
        if (this.services.connection.isConnected) {
            this.sendQuery(message);
        }
        else if (this.services.connection.isInLimbo) {
            this.limboQueue.push(message);
        }
        else {
            this.services.timerRegistry.requestIdleCallback(function () {
                emitter.emit(emitterAction, constants_1.EVENT.CLIENT_OFFLINE);
            });
        }
        if (callback) {
            emitter.once(emitterAction, callback);
            return;
        }
        return new Promise(function (resolve, reject) {
            emitter.once(emitterAction, function (error, results) { return error ? reject(error) : resolve(results); });
        });
    };
    PresenceHandler.prototype.handle = function (message) {
        if (message.isAck) {
            this.services.timeoutRegistry.remove(message);
            return;
        }
        if (message.action === constants_1.PRESENCE_ACTION.QUERY_ALL_RESPONSE) {
            this.queryAllEmitter.emit(ONLY_EVENT, null, message.names);
            this.services.timeoutRegistry.remove(message);
            return;
        }
        if (message.action === constants_1.PRESENCE_ACTION.QUERY_RESPONSE) {
            this.queryEmitter.emit(message.correlationId, null, message.parsedData);
            this.services.timeoutRegistry.remove(message);
            return;
        }
        if (message.action === constants_1.PRESENCE_ACTION.PRESENCE_JOIN) {
            this.subscriptionEmitter.emit(message.name, message.name, true);
            return;
        }
        if (message.action === constants_1.PRESENCE_ACTION.PRESENCE_JOIN_ALL) {
            this.globalSubscriptionEmitter.emit(ONLY_EVENT, message.name, true);
            return;
        }
        if (message.action === constants_1.PRESENCE_ACTION.PRESENCE_LEAVE) {
            this.subscriptionEmitter.emit(message.name, message.name, false);
            return;
        }
        if (message.action === constants_1.PRESENCE_ACTION.PRESENCE_LEAVE_ALL) {
            this.globalSubscriptionEmitter.emit(ONLY_EVENT, message.name, false);
            return;
        }
        if (message.isError) {
            this.services.timeoutRegistry.remove(message);
            if (message.originalAction === constants_1.PRESENCE_ACTION.QUERY) {
                this.queryEmitter.emit(message.correlationId, constants_1.PRESENCE_ACTION[message.action]);
            }
            else if (message.originalAction === constants_1.PRESENCE_ACTION.QUERY_ALL) {
                this.queryAllEmitter.emit(ONLY_EVENT, constants_1.PRESENCE_ACTION[message.action]);
            }
            else {
                this.services.logger.error(message);
            }
            return;
        }
        this.services.logger.error(message, constants_1.EVENT.UNSOLICITED_MESSAGE);
    };
    PresenceHandler.prototype.sendQuery = function (message) {
        this.services.connection.sendMessage(message);
        this.services.timeoutRegistry.add({ message: message });
    };
    PresenceHandler.prototype.subscribeToAllChanges = function () {
        if (!this.services.connection.isConnected) {
            return;
        }
        var message = { topic: constants_1.TOPIC.PRESENCE, action: constants_1.PRESENCE_ACTION.SUBSCRIBE_ALL };
        this.services.timeoutRegistry.add({ message: message });
        this.services.connection.sendMessage(message);
    };
    PresenceHandler.prototype.unsubscribeToAllChanges = function () {
        if (!this.services.connection.isConnected) {
            return;
        }
        var message = { topic: constants_1.TOPIC.PRESENCE, action: constants_1.PRESENCE_ACTION.UNSUBSCRIBE_ALL };
        this.services.timeoutRegistry.add({ message: message });
        this.services.connection.sendMessage(message);
    };
    PresenceHandler.prototype.onConnectionReestablished = function () {
        var keys = this.subscriptionEmitter.eventNames();
        if (keys.length > 0) {
            this.bulkSubscription.subscribeList(keys);
        }
        var hasGlobalSubscription = this.globalSubscriptionEmitter.hasListeners(ONLY_EVENT);
        if (hasGlobalSubscription) {
            this.subscribeToAllChanges();
        }
        for (var i = 0; i < this.limboQueue.length; i++) {
            this.sendQuery(this.limboQueue[i]);
        }
        this.limboQueue = [];
    };
    PresenceHandler.prototype.onExitLimbo = function () {
        var _this = this;
        this.queryEmitter.eventNames().forEach(function (correlationId) {
            _this.queryEmitter.emit(correlationId, constants_1.EVENT.CLIENT_OFFLINE);
        });
        this.queryAllEmitter.emit(ONLY_EVENT, constants_1.EVENT.CLIENT_OFFLINE);
        this.limboQueue = [];
        this.queryAllEmitter.off();
        this.queryEmitter.off();
    };
    PresenceHandler.prototype.onBulkSubscriptionSent = function (message) {
        this.services.timeoutRegistry.add({ message: message });
    };
    return PresenceHandler;
}());
exports.PresenceHandler = PresenceHandler;
